import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@eika/button';
import { InfoCard } from '@eika/card';
import { useViewType, ViewType } from '@eika/hooks';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import RecurringPayment from '../../components/RecurringPayment/RecurringPayment';
import RecurringPaymentSkeleton from '../../components/RecurringPayment/RecurringPaymentSkeleton';
import SkipLink from '../../components/SkipLink/SkipLink';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import RecurringPaymentDetails from '../../components/transaction/RecurringPayment/RecurringPaymentDetails';
import RecurringTransferDetails from '../../components/transaction/RecurringTransfer/RecurringTransferDetails';
import { useRecurringPayments } from '../../fetchHooks/useRecurringPayments';
import type { PaymentAgreementId } from '../../generated-types';
import useQueryParam from '../../hooks/queryparams/useQueryParam';
import useTranslation from '../../hooks/useTranslation';
import { RecurringPaymentsActions } from '../RecurringPaymentsActions';

import './RecurringPayments.scss';

const RecurringPayments: React.FC = () => {
    const translated = useTranslation();
    const viewType = useViewType();
    const recurringPaymentSearchParamId = useQueryParam('recurringpaymentid');

    const { data: recurringPayments, isLoading, isRefetching, refetch, error } = useRecurringPayments();
    const hasReccuringPayments = recurringPayments && recurringPayments.length !== 0;
    const [visiblePaymentId, setVisiblePaymentId] = useState<PaymentAgreementId>();

    const visiblePayment = useMemo(() => {
        return visiblePaymentId ? recurringPayments?.find((p) => p.id === visiblePaymentId) : undefined;
    }, [visiblePaymentId, recurringPayments]);

    useEffect(() => {
        setVisiblePaymentId(recurringPaymentSearchParamId);
    }, [recurringPaymentSearchParamId]);

    return (
        <Content>
            {hasReccuringPayments && viewType === ViewType.desktop && (
                <SkipLink skipTo="#recurring-payment-action-buttons" linkText={translated.recurringPayments.skipLink} />
            )}
            <RecurringPaymentsActions views={[ViewType.mobile, ViewType.pad]} />
            <h2>{translated.recurringPayments.agreements}</h2>
            {isLoading || isRefetching ? (
                <RecurringPaymentSkeleton count={recurringPayments?.length ?? 2} />
            ) : (
                <>
                    {hasReccuringPayments && (
                        <>
                            <ul className="recurring-payment__list">
                                {recurringPayments.map((recurringPayment) => (
                                    <RecurringPayment
                                        recurringPayment={recurringPayment}
                                        key={recurringPayment.id.toString()}
                                        onShow={setVisiblePaymentId}
                                    />
                                ))}
                            </ul>
                            {visiblePayment && (
                                <>
                                    {visiblePayment.paymentType === 'PAYMENT' ? (
                                        <RecurringPaymentDetails
                                            recurringPayment={visiblePayment}
                                            show
                                            onClose={() => setVisiblePaymentId(undefined)}
                                            refresh={refetch}
                                        />
                                    ) : (
                                        <RecurringTransferDetails
                                            recurringTransfer={visiblePayment}
                                            show
                                            onClose={() => setVisiblePaymentId(undefined)}
                                            refresh={refetch}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            {error && !isLoading && (
                <ErrorCardWrapper
                    className="recurring-payment__error-view"
                    exception={error}
                    defaultError={translated.recurringPayments.errorMessage}
                >
                    <Button onClick={() => refetch()}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            )}
            {!hasReccuringPayments && !isLoading && !error && (
                <InfoCard className="recurring-payment__no-payments" data-testid="no-recurring-payments" skipBackground>
                    <RichIcon type={RichIconTypes.BAAT_M_SOL} />
                    <InfoCard.TitleH2>{translated.recurringPayments.noPayments}</InfoCard.TitleH2>
                </InfoCard>
            )}
        </Content>
    );
};

export default RecurringPayments;
